import React, { useEffect, useState } from 'react'
import get from 'lodash.get'
import set from 'lodash.set'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'
import { Form, Formik } from 'formik'
import {
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  Select,
  Space,
  Form as StyleForm,
  Tabs,
  Typography,
  message,
} from 'antd'
import { formOptions, OrderFabricRefPictures } from '../../../forms/blocks'
import { updateOrder } from '../../../network/Orders'
import { getProductDetailsComplete } from '../../../network/Products'
import { getProductFabric } from '../../../network/ProductFabrics'
import { getProductPrices } from '../../../network/ProductPrices'
import { getFixSkuById } from '../../../network/FixSku'
import { getFixedDesigns } from '../../../network/FixedDesign'
import FormContent from '../FormContent'
import {
  getFromKey,
  getCollectFrom,
  getCollectFromForm,
  getFixedDesignCategory,
  getCategoryFromFixedDesign,
  getStockForSelectedSize,
  getRequestedFixSkuQtyForSize,
  formatSelectedOptions,
  selectedOptionsToMap,
  FIXED_DESIGN_OTHER_OPTION_VALUE,
} from '../utils/orderHelpers'
import { scrollToTop } from '../../../utils/utils'

const { Text } = Typography

const EditOrderEditOrderInfos = React.memo(({ closeMeasure, data, productIndex, initialTab }) => {
  let { orderId } = useParams()
  const queryClient = useQueryClient()
  const [activeKey, setActiveKey] = useState(initialTab ?? '0')
  // Sync tab when initialTab is set
  useEffect(() => {
    const tab = initialTab ?? '0'
    setActiveKey(tab)
  }, [initialTab])
  // Scroll to top when opening edit view
  useEffect(() => {
    let timeoutId
    const rafId = requestAnimationFrame(() => {
      scrollToTop()
      timeoutId = setTimeout(scrollToTop, 100)
    })
    return () => {
      cancelAnimationFrame(rafId)
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [])
  const [shirtFabricOptions, setShirtFabricOptions] = useState([
    { key: 'others', label: 'Others' },
  ])
  const [productPriceLists, setProductPriceLists] = useState({})

  const isCollection =
    getFromKey(data.orderProducts[productIndex]) === 'collection'

  const productDetails = useQuery(
    [
      'getProductDetailsComplete',
      {
        id: get(
          data,
          `orderProducts.${productIndex}.orderCollection.product`,
          ''
        ),
      },
    ],
    getProductDetailsComplete,
    {
      refetchOnWindowFocus: false,
      enabled: isCollection,
    }
  )

  const editOrder = useMutation((val) => updateOrder(...val), {
    onSuccess: () => {
      message.success('Order updated !')
      queryClient.invalidateQueries(['getOrderDetails', { id: orderId }])
      closeMeasure()
    },
  })

  const fixedDesignsQuery = useQuery(
    ['getFixedDesigns', { sort: [] }],
    getFixedDesigns,
    {
      refetchOnWindowFocus: false,
      enabled:
        getFromKey(data.orderProducts[productIndex]) === 'fixed_design',
    }
  )

  const productTypeKeyForForms =
    getFromKey(data.orderProducts[productIndex]) === 'fixed_design'
      ? getFixedDesignCategory(data.orderProducts[productIndex])
      : getFromKey(data.orderProducts[productIndex])

  const formsInf = isCollection
    ? getCollectFrom(get(productDetails, 'data.category.blocks', []))
    : formOptions[productTypeKeyForForms]?.measuresForms

  const formsInfF = isCollection
    ? getCollectFromForm(get(productDetails, 'data.category.blocks', []))
    : formOptions[productTypeKeyForForms]?.forms

  // Fetch product prices from API (used for quality/price selectors instead of hardcoded formOptions)
  useEffect(() => {
    getProductPrices()
      .then((res) => {
        const list = res?.list ?? res?.data?.list ?? res
        const products = list?.products ?? []
        const byKey = {}
        products.forEach((product) => {
          if (product?.key && Array.isArray(product.pricing)) {
            byKey[product.key] = product.pricing.reduce((acc, p) => {
              acc[p.key] = { price: p.price, label: p.label ?? p.key }
              return acc
            }, {})
          }
        })
        setProductPriceLists(byKey)
      })
      .catch((err) => console.error('EditOrder - Error fetching product prices:', err))
  }, [])

  // Resolve price list: prefer API productPriceLists, fallback to formOptions (e.g. collection)
  const getPriceList = (category) =>
    productPriceLists[category] ?? formOptions[category]?.priceList

  // Fetch fabric options for shirt products
  useEffect(() => {
    const productType = getFromKey(data.orderProducts[productIndex])
    if (productType === 'shirt') {
      const fetchFabricOptions = async () => {
        try {
          const fabricResponse = await getProductFabric('shirt')

          const fabrics =
            fabricResponse?.product?.fabrics || fabricResponse?.fabrics || []
          const fabricOptions = [
            ...fabrics.map((fabric) => ({
              key: fabric.key,
              label: fabric.label,
            })),
            { key: 'others', label: 'Others' },
          ]

          setShirtFabricOptions(fabricOptions)
        } catch (error) {
          console.error('EditOrder - Error fetching fabric:', error)
        }
      }

      fetchFabricOptions()
    }
  }, [data.orderProducts, productIndex])

  return (
    <Formik
      initialValues={data}
      // validate={() => {
      //   // Why have no validation here ?
      //   // const errors = {}
      //   // if (values.balance < 0) {
      //   //   errors.balance = 'Balance must be greater than 0'
      //   // }
      //   // return errors
      // }}

      validate={(values) => {
        const errors = {}
        const innerStep = productIndex

        // innerStep
        const path = ['orderProducts', innerStep]
        const currentProduct = values.orderProducts[innerStep]

        if (!currentProduct) return

        if (currentProduct.orderProductType === 'collection') {
          // to do check collection errors
          const collectionType = get(
            currentProduct,
            'orderCollection.productType',
            []
          )

          for (const prodKey of collectionType) {
            get(formOptions, `${prodKey}.measuresForms`, []).forEach((form) =>
              // formOptions[prodKey].measuresForms.forEach((form) =>
              form.form.forEach((prodForm) =>
                prodForm.sections.forEach((section) =>
                  section.fields.forEach((field) => {
                    const name = [...path, form.key, ...prodForm.key].join('.')
                    const fieldValue = get(
                      values,
                      name + '.' + field.key.join('.'),
                      ''
                    )

                    const fieldIsRequired = field.isConditionalRequired
                      ? field.isConditionalRequired(values, name + '.')
                      : !!field.isRequired

                    if (fieldIsRequired && !fieldValue && fieldValue !== 0) {
                      set(
                        errors,
                        name + '.' + field.key.join('.'),
                        'This field is required'
                      )
                    }
                  })
                )
              )
            )
          }
        } else if (currentProduct.orderProductType === 'adjustment') {
          // formOptions[currentProduct.type].measuresForms.forEach((form) =>
          //   form.form.forEach((prodForm) =>
          //     prodForm.sections.forEach((section) =>
          //       section.fields.forEach((field) => {
          //         const fieldValue = get(
          //           values,
          //           [
          //             ...path,
          //             form.key.replace(
          //               'orderCustom.',
          //               'orderAdjustment.'
          //             ),
          //             ...prodForm.key,
          //             ...field.key,
          //           ].join('.'),
          //           ''
          //         )
          //         if (
          //           field.isRequired &&
          //           !fieldValue &&
          //           fieldValue !== 0
          //         ) {
          //           set(
          //             errors,
          //             [
          //               ...path,
          //               form.key.replace(
          //                 'orderCustom.',
          //                 'orderAdjustment.'
          //               ),
          //               ...prodForm.key,
          //               ...field.key,
          //             ].join('.'),
          //             'This field is required'
          //           )
          //         }
          //       })
          //     )
          //   )
          // )
        } else {
          // TODO: refactor this in the future, this is ugly

          /// Jacket
          // "Waist" and "Chest" should have less than 18 cm difference
          const jacketWaist = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementJacketBlazer.waist`,
            0
          )
          const jacketChest = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementJacketBlazer.chest`,
            0
          )
          if (Math.abs(jacketWaist - jacketChest) > 18) {
            set(
              errors,
              `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementJacketBlazer.waist`,
              'Waist and chest difference should be less than 18cm'
            )
          }

          /// Vest
          // The difference between "vest length front straight"
          // and "vest length back straight" should be less than 1cm
          const vestLengthFrontStraight = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementVest.lengthFrontStraight`,
            0
          )
          const vestLengthBackStraight = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementVest.lengthBackStraight`,
            0
          )
          const vestBottomShape = get(
            currentProduct,
            'orderCustom.orderCustomVest.vestBottomShape',
            ''
          )
          if (
            vestBottomShape === 'straight_bottom' &&
            vestLengthFrontStraight &&
            vestLengthBackStraight &&
            Math.abs(vestLengthFrontStraight - vestLengthBackStraight) > 1
          ) {
            set(
              errors,
              `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementVest.lengthFrontStraight`,
              'Front and back length difference should be less than 1cm'
            )
          }
          // The difference between "vest length front cross"
          // and "vest length back cross" should be less than 6cm
          const vestLengthFrontCross = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementVest.lengthFrontCross`,
            0
          )
          const vestLengthBackCross = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementVest.lengthBackCross`,
            0
          )
          if (
            vestBottomShape === 'cross_arrow' &&
            vestLengthFrontCross &&
            vestLengthBackCross &&
            Math.abs(vestLengthFrontCross - vestLengthBackCross) > 6
          ) {
            set(
              errors,
              `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementVest.lengthFrontCross`,
              'Front and back length difference should be less than 6cm'
            )
          }
          // The difference between "chest" and "waist" should be larger than 12 cm
          const vestChest = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementVest.chest`,
            0
          )
          const vestWaist = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementVest.waist`,
            0
          )
          if (vestChest && vestWaist && Math.abs(vestChest - vestWaist) > 12) {
            set(
              errors,
              `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementVest.chest`,
              'Chest and waist difference cannot be larger than 12cm'
            )
          }

          /// Shirt
          // The diff between lengthFront and lengthBack should be less than 18cm
          const shirtLengthFront = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementShirt.lengthFront`,
            0
          )
          const shirtLengthBack = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementShirt.lengthBack`,
            0
          )
          if (Math.abs(shirtLengthFront - shirtLengthBack) > 18) {
            set(
              errors,
              `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementShirt.lengthFront`,
              'Front and back length difference should be less than 18cm'
            )
          }
          // Prefill "shirt length back" depending on "shirt length front"
          if (shirtLengthFront && !shirtLengthBack) {
            set(
              values,
              `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementShirt.lengthBack`,
              shirtLengthFront
            )
          }
          // The diff between chest and waist should be less than 18cm
          const shirtChest = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementShirt.chest`,
            0
          )
          const shirtWaist = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementShirt.waist`,
            0
          )
          if (
            shirtChest &&
            shirtWaist &&
            Math.abs(shirtChest - shirtWaist) > 18
          ) {
            set(
              errors,
              `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementShirt.chest`,
              'Chest and waist difference should be less than 18cm'
            )
          }
          //  Prefill the "Shirt sleeve wrist (left)"
          //  depending on the field "Shirt sleeve wrist (right)" -> Can be modified
          const shirtSleeveWristRight = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementShirt.sleeveWristRight`,
            0
          )
          const shirtSleeveWristLeft = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementShirt.sleeveWristLeft`,
            0
          )
          if (shirtSleeveWristLeft && !shirtSleeveWristRight) {
            set(
              values,
              `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementShirt.sleeveWristRight`,
              shirtSleeveWristLeft
            )
          }

          /// Jeans
          // The sum of "front crotch" and "back crotch" need to be equal with "whole crotch"
          const jeansFrontCrotch = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementJean.frontCrotch`,
            0
          )
          const jeansBackCrotch = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementJean.backCrotch`,
            0
          )
          const jeansWholeCrotch = get(
            currentProduct,
            `skuMeasurements.0.skuMeasurementJean.crotch`,
            0
          )
          if (
            jeansFrontCrotch &&
            jeansBackCrotch &&
            jeansWholeCrotch &&
            jeansFrontCrotch + jeansBackCrotch !== jeansWholeCrotch
          ) {
            set(
              errors,
              `orderProducts.${innerStep}.skuMeasurements.0.skuMeasurementJean.crotch`,
              'The sum of "front crotch" and "back crotch" need to be equal with "whole crotch"'
            )
          }

          get(
            formOptions,
            `${getFromKey(currentProduct)}.measuresForms`,
            []
          ).forEach(
            // formOptions[getFromKey(currentProduct)].measuresForms.forEach(
            (form) =>
              form.form.forEach((prodForm) =>
                prodForm.sections.forEach((section) =>
                  section.fields.forEach((field) => {
                    const name = [...path, form.key, ...prodForm.key].join('.')

                    const fieldValue = get(
                      values,
                      name + '.' + field.key.join('.'),
                      ''
                    )

                    const fieldIsRequired = field.isConditionalRequired
                      ? field.isConditionalRequired(values, name + '.')
                      : !!field.isRequired

                    if (fieldIsRequired && !fieldValue && fieldValue !== 0) {
                      set(
                        errors,
                        name + '.' + field.key.join('.'),
                        'This field is required'
                      )
                    }
                  })
                )
              )
          )
        }

        return errors
      }}
      onSubmit={async (values) => {
        let newValues = JSON.stringify(values)
        newValues = JSON.parse(newValues)
        get(newValues, `orderProducts`, []).forEach((prod, idx) => {
          if (prod.orderProductType === 'collection') {

            if (idx === productIndex && prod.orderCollection.options) {
              const optionsTemp = prod.orderCollection.optionsTemp || []
              const optionsFreeText = prod.orderCollection.optionsFreeText || []

              prod.orderCollection.options.forEach((option, jdx) => {
                const selectedValue = optionsTemp[jdx]
                const freeTextValue = optionsFreeText[jdx]
                const isOtherSelected =
                  selectedValue && selectedValue.startsWith('other-')

                // First, mark all existing subOptions as not selected
                option.subOptions.forEach((subOption, kdx) => {
                  set(
                    newValues,
                    `orderProducts.${idx}.orderCollection.options.${jdx}.subOptions.${kdx}.selected`,
                    false
                  )
                })

                // If "other" is selected, create/update custom subOption
                if (isOtherSelected && freeTextValue) {
                  // Check if custom subOption already exists
                  const existingCustomIndex = option.subOptions.findIndex(
                    (sub) => sub.ref === 'custom'
                  )

                  if (existingCustomIndex !== -1) {
                    // Update existing custom subOption
                    set(
                      newValues,
                      `orderProducts.${idx}.orderCollection.options.${jdx}.subOptions.${existingCustomIndex}.name`,
                      {
                        en: freeTextValue,
                        zh: freeTextValue,
                      }
                    )
                    set(
                      newValues,
                      `orderProducts.${idx}.orderCollection.options.${jdx}.subOptions.${existingCustomIndex}.selected`,
                      true
                    )
                  } else {
                    // Add new custom subOption
                    const newSubOption = {
                      name: {
                        en: freeTextValue,
                        zh: freeTextValue,
                      },
                      ref: 'custom',
                      selected: true,
                    }
                    const currentSubOptions = get(
                      newValues,
                      `orderProducts.${idx}.orderCollection.options.${jdx}.subOptions`,
                      []
                    )
                    set(
                      newValues,
                      `orderProducts.${idx}.orderCollection.options.${jdx}.subOptions`,
                      [...currentSubOptions, newSubOption]
                    )
                  }
                } else {
                  // Mark regular subOption as selected
                  option.subOptions.forEach((subOption, kdx) => {
                    if (selectedValue === subOption._id) {
                      set(
                        newValues,
                        `orderProducts.${idx}.orderCollection.options.${jdx}.subOptions.${kdx}.selected`,
                        true
                      )
                    }
                  })
                }
              })
            }
          }

        })

        // totalSpent and balance are computed server-side from orderProducts
        editOrder.mutate([newValues, orderId])
      }}
    >
      {({ values, errors, handleChange }) => (
        <Form>
          <Tabs
            className="orders-tabs"
            activeKey={activeKey}
            defaultActiveKey="0"
            onChange={setActiveKey}
            items={[
              {
                key: '0',
                label: 'Options',
                children: (
                  <div style={{ padding: '0px 24px' }}>
              {get(
                values,
                `orderProducts.${productIndex}.orderProductType`,
                false
              ) === 'collection' ? (
                <CollectionForm
                  values={values}
                  errors={{}}
                  path={`orderProducts.${productIndex}`}
                  handleChange={(e) => handleChange(e)}
                />
              ) : get(
                  values,
                  `orderProducts.${productIndex}.orderProductType`,
                  false
                ) === 'fixed_design' ? (
                (() => {
                  const category = getFixedDesignCategory(
                    data.orderProducts[productIndex]
                  )
                  const allFixedDesigns = get(
                    fixedDesignsQuery.data,
                    'list',
                    get(
                      fixedDesignsQuery.data,
                      'data.list',
                      Array.isArray(fixedDesignsQuery.data)
                        ? fixedDesignsQuery.data
                        : []
                    )
                  )
                  const fixedDesignProductsForCategory = (
                    Array.isArray(allFixedDesigns) ? allFixedDesigns : []
                  ).filter(
                    (fd) => getCategoryFromFixedDesign(fd) === category
                  )
                  const selectedProduct =
                    fixedDesignProductsForCategory.find(
                      (p) =>
                        p.id ===
                        get(
                          values,
                          `orderProducts.${productIndex}.orderFixedDesign.productId`
                        )
                    )
                  const optionsMap = selectedOptionsToMap(
                    get(
                      values,
                      `orderProducts.${productIndex}.orderFixedDesign.selectedOptions`,
                      []
                    )
                  )
                  const currencyLabel =
                    data.currency?.toUpperCase?.() ?? 'CNY'

                  if (fixedDesignsQuery.isLoading) {
                    return (
                      <StyleForm
                        component="div"
                        layout="vertical"
                        style={{ width: '100%' }}
                      >
                        <Card title="Fixed Design Product">
                          <Space direction="vertical">
                            <Text type="secondary">
                              Loading fixed designs…
                            </Text>
                          </Space>
                        </Card>
                      </StyleForm>
                    )
                  }

                  return (
                    <StyleForm
                      component="div"
                      layout="vertical"
                      style={{ width: '100%' }}
                    >
                      {fixedDesignProductsForCategory.length > 0 && (
                        <>
                          <Card
                            title={`Fixed design – ${
                              formOptions[category]?.name ?? category ?? ''
                            }`}
                            style={{ marginBottom: 16 }}
                          >
                            <Space
                              size={0}
                              direction="vertical"
                              style={{ width: '100%' }}
                            >
                              <StyleForm.Item label="Fixed design product">
                                <Select
                                  style={{ width: '50%' }}
                                  placeholder="Select a fixed design product"
                                  value={selectedProduct?.id}
                                  onChange={(productId) => {
                                    const product =
                                      fixedDesignProductsForCategory.find(
                                        (p) => p.id === productId
                                      )
                                    if (product) {
                                      const priceList = getPriceList(category)
                                      const firstKey = priceList
                                        ? Object.keys(priceList)[0]
                                        : null
                                      const initialPrice = firstKey
                                        ? priceList[firstKey].price
                                        : product.retailPrice ?? product.price
                                      handleChange({
                                        target: {
                                          name: `orderProducts.${productIndex}`,
                                          value: {
                                            ...values.orderProducts[
                                              productIndex
                                            ],
                                            orderProductType: 'fixed_design',
                                            orderFixedDesign: {
                                              productId: product.id,
                                              productName: product.name,
                                              sku: product.sku,
                                              price: initialPrice,
                                              quality: firstKey ?? undefined,
                                              deliveryTime: product.deliveryTime,
                                              description: product.description,
                                              details: product.details,
                                              selectedOptions: [],
                                              personaeType:
                                                get(
                                                  values,
                                                  `orderProducts.${productIndex}.orderFixedDesign.personaeType`
                                                ) ?? 'special_event',
                                            },
                                          },
                                        },
                                      })
                                    }
                                  }}
                                >
                                  {fixedDesignProductsForCategory.map(
                                    (product, idx) => (
                                      <Select.Option
                                        key={product.id ?? `fd-${idx}`}
                                        value={product.id}
                                      >
                                        {[product.name?.en || product.name?.zh, product.sku]
                                          .filter(Boolean)
                                          .join(' – ')}
                                      </Select.Option>
                                    )
                                  )}
                                </Select>
                              </StyleForm.Item>
                            </Space>
                          </Card>

                          {getPriceList(category) && (
                            <Card
                              title={`Price ${currencyLabel}`}
                              style={{ marginBottom: 16 }}
                            >
                              <Space
                                size={0}
                                direction="vertical"
                                style={{ width: '100%' }}
                              >
                                <StyleForm.Item>
                                  {(() => {
                                    const priceList = getPriceList(category) ?? {}
                                    const qualityValue = get(
                                      values,
                                      `orderProducts.${productIndex}.orderFixedDesign.quality`,
                                      undefined
                                    )
                                    const currentPrice = Number(
                                      get(
                                        values,
                                        `orderProducts.${productIndex}.orderFixedDesign.price`,
                                        0
                                      )
                                    )
                                    // Backward-compatibility for older orders that can have price set but missing/unknown quality key
                                    const fallbackQualityByPrice = Object.keys(
                                      priceList
                                    ).find((k) => {
                                      const p = Number(priceList[k]?.price)
                                      return !Number.isNaN(p) && p === currentPrice
                                    })
                                    const resolvedQuality =
                                      qualityValue && priceList[qualityValue]
                                        ? qualityValue
                                        : fallbackQualityByPrice

                                    return (
                                  <Select
                                    style={{ width: '50%' }}
                                    placeholder="Select price"
                                    value={resolvedQuality}
                                    onChange={(key) => {
                                      const selectedPrice = priceList?.[key]?.price
                                      handleChange({
                                        target: {
                                          name: `orderProducts.${productIndex}.orderFixedDesign.quality`,
                                          value: key,
                                        },
                                      })
                                      handleChange({
                                        target: {
                                          name: `orderProducts.${productIndex}.orderFixedDesign.price`,
                                          value: selectedPrice ?? 0,
                                        },
                                      })
                                    }}
                                  >
                                    {Object.keys(priceList).map((k) => (
                                        <Select.Option key={k} value={k}>
                                          {priceList?.[k]?.label ?? k}
                                      </Select.Option>
                                    ))}
                                  </Select>
                                    )
                                  })()}
                                </StyleForm.Item>
                                <StyleForm.Item
                                  validateStatus={
                                    get(
                                      errors,
                                      `orderProducts.${productIndex}.orderFixedDesign.price`,
                                      false
                                    )
                                      ? 'error'
                                      : null
                                  }
                                  help={get(
                                    errors,
                                    `orderProducts.${productIndex}.orderFixedDesign.price`,
                                    null
                                  )}
                                >
                                  <Input
                                    style={{ width: '50%' }}
                                    value={get(
                                      values,
                                      `orderProducts.${productIndex}.orderFixedDesign.price`,
                                      ''
                                    )}
                                    disabled
                                  />
                                </StyleForm.Item>
                              </Space>
                            </Card>
                          )}

{selectedProduct?.options &&
                            Array.isArray(selectedProduct.options) &&
                            selectedProduct.options.length > 0 && (
                              <Card title="Options" style={{ marginBottom: 16 }}>
                                <Space
                                  direction="vertical"
                                  style={{ width: '100%' }}
                                  size="middle"
                                >
                                  {selectedProduct.options.map(
                                    (option, optionIndex) => {
                                      const optionLabel =
                                        option.name?.en ||
                                        option.name?.zh ||
                                        `Option ${optionIndex + 1}`
                                      const hasSubOptions =
                                        Array.isArray(option.subOptions) &&
                                        option.subOptions.length > 0
                                      return (
                                        <div
                                          key={
                                            option._id ??
                                            `option-${optionIndex}`
                                          }
                                        >
                                          <StyleForm.Item label={optionLabel}>
                                            {option.freeField && !hasSubOptions ? (
                                              <Input
                                                placeholder={`Enter ${optionLabel}`}
                                                value={
                                                  optionsMap[`${option._id}__freeFieldValue`] ??
                                                  optionsMap[option._id] ??
                                                  ''
                                                }
                                                onChange={(e) => {
                                                  const newMap = {
                                                    ...optionsMap,
                                                    [option._id]: FIXED_DESIGN_OTHER_OPTION_VALUE,
                                                    [`${option._id}__freeFieldValue`]: e.target.value,
                                                  }
                                                  handleChange({
                                                    target: {
                                                      name: `orderProducts.${productIndex}.orderFixedDesign.selectedOptions`,
                                                      value: formatSelectedOptions(
                                                        newMap,
                                                        selectedProduct
                                                      ),
                                                    },
                                                  })
                                                }}
                                              />
                                            ) : (
                                              <>
                                                <Select
                                                  style={{ width: '100%' }}
                                                  placeholder={`Select ${optionLabel}`}
                                                  value={
                                                    optionsMap[option._id] ?? undefined
                                                  }
                                                  onChange={(value) => {
                                                    const newMap = {
                                                      ...optionsMap,
                                                      [option._id]: value,
                                                    }
                                                    if (value !== FIXED_DESIGN_OTHER_OPTION_VALUE) {
                                                      delete newMap[`${option._id}__freeFieldValue`]
                                                    }
                                                    handleChange({
                                                      target: {
                                                        name: `orderProducts.${productIndex}.orderFixedDesign.selectedOptions`,
                                                        value: formatSelectedOptions(
                                                          newMap,
                                                          selectedProduct
                                                        ),
                                                      },
                                                    })
                                                  }}
                                                >
                                                  {option.subOptions?.map(
                                                    (subOption, subIdx) => {
                                                      const subLabel =
                                                        subOption.name?.en ||
                                                        subOption.name?.zh ||
                                                        subOption.ref
                                                      return (
                                                        <Select.Option
                                                          key={
                                                            subOption._id ??
                                                            `sub-${subIdx}`
                                                          }
                                                          value={subOption.ref}
                                                        >
                                                          {subLabel}
                                                          {subOption.ref
                                                            ? ` (Ref: ${subOption.ref})`
                                                            : ''}
                                                        </Select.Option>
                                                      )
                                                    }
                                                  )}
                                                  {option.freeField && (
                                                    <Select.Option value={FIXED_DESIGN_OTHER_OPTION_VALUE}>
                                                      Other
                                                    </Select.Option>
                                                  )}
                                                </Select>
                                                {option.freeField &&
                                                  optionsMap[option._id] === FIXED_DESIGN_OTHER_OPTION_VALUE && (
                                                    <Input
                                                      style={{ marginTop: 8 }}
                                                      placeholder={`Enter ${optionLabel}`}
                                                      value={
                                                        optionsMap[`${option._id}__freeFieldValue`] ?? ''
                                                      }
                                                      onChange={(e) => {
                                                        const newMap = {
                                                          ...optionsMap,
                                                          [option._id]: FIXED_DESIGN_OTHER_OPTION_VALUE,
                                                          [`${option._id}__freeFieldValue`]: e.target.value,
                                                        }
                                                        handleChange({
                                                          target: {
                                                            name: `orderProducts.${productIndex}.orderFixedDesign.selectedOptions`,
                                                            value: formatSelectedOptions(
                                                              newMap,
                                                              selectedProduct
                                                            ),
                                                          },
                                                        })
                                                      }}
                                                    />
                                                  )}
                                              </>
                                            )}
                                          </StyleForm.Item>
                                        </div>
                                      )
                                    }
                                  )}
                                </Space>
                              </Card>
                            )}

                          {/* Product details (e.g. vents) filtered by _displayFlags */}
                          {(() => {
                            const detail0 = get(
                              values,
                              `orderProducts.${productIndex}.orderFixedDesign.details.0`,
                              {}
                            )
                            const productTypeKey =
                              Object.keys(detail0).find((k) => k !== '_id') ||
                              getFixedDesignCategory(
                                data.orderProducts[productIndex]
                              )
                            const productData = productTypeKey
                              ? detail0[productTypeKey]
                              : null
                            const displayFlags =
                              productData?._displayFlags || {}
                            const productForms =
                              formOptions[category]?.forms
                            if (
                              !productTypeKey ||
                              !productForms?.length ||
                              Object.keys(displayFlags).length === 0
                            )
                              return null
                            const filterFormByDisplayFlags = (formItem) => {
                              if (!formItem?.sections) return formItem
                              return {
                                ...formItem,
                                sections: formItem.sections
                                  .map((section) => ({
                                    ...section,
                                    fields: (
                                      section.fields || []
                                    ).filter((field) => {
                                      const key = field.key?.[0]
                                      return (
                                        key && displayFlags[key] === true
                                      )
                                    }),
                                  }))
                                  .filter(
                                    (section) =>
                                      (section.fields || []).length > 0
                                  ),
                              }
                            }
                            const formGroup = productForms[0]
                            const form = formGroup.form
                            const filteredForm = Array.isArray(form)
                              ? form.map((f) =>
                                  filterFormByDisplayFlags(f)
                                )
                              : filterFormByDisplayFlags(form)
                            let formArray = Array.isArray(filteredForm)
                              ? filteredForm
                              : [filteredForm]
                            formArray = formArray.filter(
                              (f) =>
                                f.title !== 'Add more shirts' &&
                                !f.sections?.some(
                                  (s) =>
                                    s.key &&
                                    s.key.join?.('.') === 'extraShirts'
                                )
                            )
                            if (
                              !formArray.some(
                                (f) => (f.sections?.length ?? 0) > 0
                              )
                            )
                              return null
                            const withShirtOptions =
                              category === 'shirt'
                                ? formArray.map((f) => ({
                                    ...f,
                                    sections: f.sections?.map((sec) => ({
                                      ...sec,
                                      fields: sec.fields?.map((field) =>
                                        field.key?.[0] === 'fabricRef'
                                          ? {
                                              ...field,
                                              options: shirtFabricOptions,
                                            }
                                          : field
                                      ),
                                    })),
                                  }))
                                : formArray
                            const filteredForms = [
                              {
                                ...formGroup,
                                key: `orderFixedDesign.details.0.${productTypeKey}`,
                                form: withShirtOptions,
                              },
                            ]
                            return (
                              <div
                                style={{
                                  marginTop: 16,
                                  marginBottom: 16,
                                  width: '100%',
                                }}
                              >
                                <FormContent
                                  haveOldValue
                                  values={values}
                                  errors={errors}
                                  path={`orderProducts.${productIndex}`}
                                  handleChange={handleChange}
                                  forms={filteredForms}
                                />
                              </div>
                            )
                          })()}

                          {/* Stock warning when selected size has no stock (fixed design) */}
                          {selectedProduct && (() => {
                            const product = selectedProduct
                            const detail0 = product.details?.[0]
                            const productTypeKey = detail0 && Object.keys(detail0).find((k) => k !== '_id')
                            if (!productTypeKey || !Array.isArray(product.stock) || product.stock.length === 0) return null
                            const detail = get(values, `orderProducts.${productIndex}.orderFixedDesign.details.0.${productTypeKey}`, {})
                            const sizeValue = detail.size ?? detail.otherSize ?? detail.shoeSize ?? detail.readyToWearSize ?? detail.tailorMadeSize
                            if (sizeValue == null || sizeValue === '') return null
                            const stock = getStockForSelectedSize(product, sizeValue)
                            if (stock === null || stock > 0) return null
                            return (
                              <Alert
                                type="warning"
                                message="The stock for this size is empty."
                                showIcon
                                style={{ marginBottom: 16 }}
                              />
                            )
                          })()}
                        </>
                      )}

                      {fixedDesignProductsForCategory.length === 0 && (
                        <Card title="Fixed Design Product">
                          <Space direction="vertical">
                            <Text type="secondary">
                              No fixed designs for this category or product not
                              found.
                            </Text>
                            <Text>
                              <strong>Product: </strong>
                              {get(
                                values,
                                `orderProducts.${productIndex}.orderFixedDesign.productName.en`,
                                get(
                                  values,
                                  `orderProducts.${productIndex}.orderFixedDesign.productName.zh`,
                                  ''
                                )
                              )}
                            </Text>
                            <Text>
                              <strong>Price: </strong>
                              {currencyLabel}{' '}
                              {get(
                                values,
                                `orderProducts.${productIndex}.orderFixedDesign.price`,
                                0
                              )}
                            </Text>
                          </Space>
                        </Card>
                      )}
                    </StyleForm>
                  )
                })()
              ) : (
                <>
                  {/* */}
                  <StyleForm component="div" layout="vertical" style={{ width: '100%' }}>
                    <Card title={'Price'}>
                      <Space
                        size={0}
                        direction="vertical"
                        style={{ width: '100%' }}
                      >
                        {getPriceList(
                          getFromKey(values.orderProducts[productIndex])
                        ) ? (
                          <StyleForm.Item>
                            <Select
                              // name={`orderProducts.${index}.orderCustom.quality`}
                              style={{ width: '50%' }}
                              onChange={(e) => {
                                handleChange({
                                  target: {
                                    name: `orderProducts.${productIndex}.orderCustom.quality`,
                                    value: e,
                                  },
                                })

                                handleChange({
                                  target: {
                                    name: `orderProducts.${productIndex}.orderCustom.price`,
                                    value:
                                      getPriceList(
                                        getFromKey(
                                          values.orderProducts[productIndex]
                                        )
                                      )?.[e]?.price ?? 0,
                                  },
                                })
                              }}
                              value={get(
                                values,
                                `orderProducts.${productIndex}.orderCustom.quality`,
                                ''
                              )}
                            >
                              {Object.keys(
                                getPriceList(
                                  getFromKey(values.orderProducts[productIndex])
                                ) ?? {}
                              ).map((key) => (
                                <Select.Option key={key} value={key}>
                                  {
                                    getPriceList(
                                      getFromKey(
                                        values.orderProducts[productIndex]
                                      )
                                    )?.[key]?.label ?? key
                                  }
                                </Select.Option>
                              ))}
                            </Select>
                          </StyleForm.Item>
                        ) : null}
                        <StyleForm.Item
                          validateStatus={
                            get(
                              errors,
                              `orderProducts.${productIndex}.orderCustom.price`,
                              false
                            )
                              ? 'error'
                              : null
                          }
                          help={get(
                            errors,
                            `orderProducts.${productIndex}.orderCustom.price`,
                            null
                          )}
                        >
                          <Input
                            name={`orderProducts.${productIndex}.orderCustom.price`}
                            type="text"
                            placeholder={'-'}
                            style={{ width: '50%' }}
                            onChange={handleChange}
                            value={get(
                              values,
                              `orderProducts.${productIndex}.orderCustom.price`,
                              ''
                            )}
                            disabled={
                              !formOptions[
                                getFromKey(values.orderProducts[productIndex])
                              ]?.isPriceEditable
                            }
                          />
                        </StyleForm.Item>
                      </Space>
                    </Card>
                  </StyleForm>
                  <FormContent
                    isEdition={true}
                    values={values}
                    errors={{}}
                    path={`orderProducts.${productIndex}`}
                    handleChange={handleChange}
                    forms={
                      getFromKey(data.orderProducts[productIndex]) === 'shirt'
                        ? formsInfF?.map((form) => ({
                            ...form,
                            form: form.form?.map((prodForm) => ({
                              ...prodForm,
                              sections: prodForm.sections?.map((section) => ({
                                ...section,
                                fields: section.fields?.map((field) => {
                                  if (field.key?.[0] === 'fabricRef') {
                                    return { ...field, options: shirtFabricOptions }
                                  }
                                  const priceList = getPriceList('shirt')
                                  if (
                                    section.key?.join?.('.') === 'extraShirts' &&
                                    field.key?.[0] === 'price' &&
                                    priceList
                                  ) {
                                    const priceOptions = Object.keys(priceList).map((k) => ({
                                      key: k,
                                      label: priceList[k]?.label ?? k,
                                    }))
                                    return {
                                      ...field,
                                      options: priceOptions,
                                      onChange: (value) => priceList[value]?.price ?? 0,
                                    }
                                  }
                                  return field
                                }),
                              })),
                            })),
                          }))
                        : formsInfF
                    }
                  />
                  <FormContent
                    isEdition={true}
                    values={values}
                    errors={errors}
                    path={`orderProducts.${productIndex}`}
                    handleChange={handleChange}
                    forms={[{ form: [OrderFabricRefPictures], key: '' }]}
                  />
                  <StyleForm component="div" layout="vertical" style={{ width: '100%' }}>
                    <Card title={'Persona Type'}>
                      <Space
                        size={0}
                        direction="vertical"
                        style={{ width: '100%' }}
                      ></Space>
                      <Button
                        type={
                          get(
                            values,
                            `orderProducts.${productIndex}.orderCustom.personaeType`,
                            null
                          ) === 'special_event'
                            ? 'primary'
                            : 'secondary'
                        }
                        onClick={() =>
                          handleChange({
                            target: {
                              name: `orderProducts.${productIndex}.orderCustom.personaeType`,
                              value: 'special_event',
                            },
                          })
                        }
                      >
                        Special Event
                      </Button>
                      <Button
                        type={
                          get(
                            values,
                            `orderProducts.${productIndex}.orderCustom.personaeType`,
                            null
                          ) === 'wardrobe_update'
                            ? 'primary'
                            : 'secondary'
                        }
                        onClick={() =>
                          handleChange({
                            target: {
                              name: `orderProducts.${productIndex}.orderCustom.personaeType`,
                              value: 'wardrobe_update',
                            },
                          })
                        }
                      >
                        Wardrobe Update
                      </Button>
                    </Card>
                  </StyleForm>
                </>
              )}
                  </div>
                ),
              },
              {
                key: '1',
                label: 'Measures',
                disabled: !formsInf,
                children: (
                  <div style={{ padding: '0px 24px' }}>
                    <FormContent
                values={values}
                errors={errors}
                path={`orderProducts.${productIndex}`}
                handleChange={handleChange}
                forms={
                  getFromKey(data.orderProducts[productIndex]) === 'shirt'
                    ? formsInf?.map((form) => ({
                        ...form,
                        form: form.form?.map((prodForm) => ({
                          ...prodForm,
                          sections: prodForm.sections?.map((section) => ({
                            ...section,
                            fields: section.fields?.map((field) =>
                              field.key?.[0] === 'fabricRef'
                                ? { ...field, options: shirtFabricOptions }
                                : field
                            ),
                          })),
                        })),
                      }))
                    : formsInf
                }
                    />
                  </div>
                ),
              },
            ]}
          />
          <Space style={{ padding: '8px 24px' }}>
            <Button
              loading={editOrder.isLoading}
              type="primary"
              htmlType="submit"
            >
              Save
            </Button>
            <Button disabled={editOrder.isLoading} onClick={closeMeasure}>
              Cancel
            </Button>
          </Space>
        </Form>
      )}
    </Formik>
  )
})

const CollectionForm = ({ values, path, handleChange }) => {
  const productName = `${path}.orderCollection.product`
  const productPrice = `${path}.orderCollection.price`
  const productSize = `${path}.orderCollection.size`
  const productId = get(values, productName, '')

  const product = useQuery(
    ['getFixSkuById', { id: productId }],
    () => getFixSkuById({ queryKey: ['getFixSkuById', { id: productId }] }),
    {
      refetchOnWindowFocus: false,
      enabled: !!productId,
    }
  )

  const sizes = product?.data?.sizes ?? []
  const selectedSize = get(values, productSize, undefined)
  const selectedSizeValue =
    selectedSize != null && typeof selectedSize === 'object'
      ? selectedSize.value
      : selectedSize
  const currentLineIdxRaw = path.split('.').pop()
  const currentLineIdx =
    currentLineIdxRaw != null && currentLineIdxRaw !== ''
      ? Number(currentLineIdxRaw)
      : null
  const requestedOther =
    product?.data && selectedSizeValue != null && selectedSizeValue !== ''
      ? getRequestedFixSkuQtyForSize({
          orderProducts: values.orderProducts,
          productId,
          selectedSizeValue,
          excludeIndex: currentLineIdx,
        })
      : 0
  const currentLineQtyRaw = get(values, `${path}.orderCollection.quantity`, null)
  const currentLineQty =
    typeof currentLineQtyRaw === 'number' &&
    !Number.isNaN(currentLineQtyRaw) &&
    currentLineQtyRaw > 0
      ? currentLineQtyRaw
      : 1
  const requestedQty =
    selectedSizeValue != null && selectedSizeValue !== ''
      ? requestedOther + currentLineQty
      : 0

  return (
    <StyleForm component="div" layout="vertical" style={{ width: '100%' }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Card title="Choose product">
          <Space size={0} direction="vertical" style={{ width: '100%' }}>
            <StyleForm.Item label="Product name">
              <Select
                value={productId}
                disabled
                style={{ width: '50%' }}
              >
                <Select.Option value={productId}>
                  {product?.data?.sku ? `${product.data.sku} - ` : ''}
                  {product?.data?.name?.en || product?.data?.name?.zh || productId}
                </Select.Option>
              </Select>
            </StyleForm.Item>
            {product?.data && (
              <StyleForm.Item label="Price">
                <InputNumber
                  min={0}
                  name={productPrice}
                  style={{ width: '50%' }}
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: productPrice,
                        value: e,
                      },
                    })
                  }
                  value={get(values, productPrice, null)}
                />
              </StyleForm.Item>
            )}
          </Space>
        </Card>
        {sizes.length > 0 && (
          <Card title="Size">
            <Space size={0} direction="vertical" style={{ width: '100%' }}>
              <StyleForm.Item label="Size">
                <Select
                  placeholder="Select size"
                  value={typeof selectedSize === 'object' ? selectedSize?.value : selectedSize}
                  style={{ width: '50%' }}
                  onChange={(sizeValue) => {
                    const sizeObj = sizes.find((s) => s.value === sizeValue)
                    handleChange({
                      target: {
                        name: productSize,
                        value: sizeObj
                          ? { _id: sizeObj._id ?? sizeObj.id, name: sizeObj.name ?? {}, value: sizeObj.value }
                          : null,
                      },
                    })
                  }}
                >
                  {sizes.map((size) => (
                    <Select.Option key={size._id} value={size.value}>
                      {size.name?.en || size.name?.zh || size.value}
                    </Select.Option>
                  ))}
                </Select>
              </StyleForm.Item>
              {product?.data &&
                selectedSize != null &&
                typeof selectedSize === 'object' &&
                selectedSize.value != null &&
                (() => {
                  const stock = getStockForSelectedSize(product.data, selectedSize)
                  return stock !== null && requestedQty > stock ? (
                  <Alert
                    type="warning"
                    message={`Insufficient stock for this size (available: ${stock}, in this order: ${requestedQty}).`}
                    showIcon
                  />
                ) : null
              })()}
            </Space>
          </Card>
        )}
      </Space>
    </StyleForm>
  )
}

EditOrderEditOrderInfos.displayName = 'EditOrderEditOrderInfos'

export default EditOrderEditOrderInfos
